/**
 * Utility functions for managing local storage
 */

// Constants
const USER_KEY = 'hangmanUser';
const USER_PREFIX = 'hangmanUser_';
const POINTS_PER_WIN = 10;

/**
 * Get the current user data from local storage
 * @returns {Object|null} The user data or null if not found
 */
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

/**
 * Save a user to local storage
 * @param {Object} userData - The user data to save
 */
export const saveUser = (userData) => {
  try {
    // Initialize score if not present
    if (userData.score === undefined) {
      userData.score = 0;
    }
    
    // Initialize games played and won if not present
    if (userData.gamesPlayed === undefined) {
      userData.gamesPlayed = 0;
    }
    
    if (userData.gamesWon === undefined) {
      userData.gamesWon = 0;
    }
    
    // Save as current user
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    
    // Also save in the all users collection
    localStorage.setItem(`${USER_PREFIX}${userData.twitterUsername}`, JSON.stringify(userData));
    
    return userData;
  } catch (error) {
    console.error('Error saving user data:', error);
    return null;
  }
};

/**
 * Update user stats after a game
 * @param {string} result - The game result ('won' or 'lost')
 * @returns {Object|null} The updated user data or null if error
 */
export const updateUserStats = (result) => {
  try {
    const userData = getCurrentUser();
    
    if (userData) {
      userData.gamesPlayed = (userData.gamesPlayed || 0) + 1;
      
      if (result === 'won') {
        userData.gamesWon = (userData.gamesWon || 0) + 1;
        userData.score = (userData.score || 0) + POINTS_PER_WIN;
      }
      
      return saveUser(userData);
    }
    
    return null;
  } catch (error) {
    console.error('Error updating user stats:', error);
    return null;
  }
};

/**
 * Get all users from local storage
 * @returns {Array} Array of user objects
 */
export const getAllUsers = () => {
  try {
    const allUsers = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(USER_PREFIX)) {
        try {
          const userData = JSON.parse(localStorage.getItem(key));
          if (userData && userData.twitterUsername) {
            // Initialize score and stats if not present
            if (userData.score === undefined) {
              userData.score = 0;
              if (userData.gamesWon) {
                userData.score = userData.gamesWon * POINTS_PER_WIN;
              }
              localStorage.setItem(key, JSON.stringify(userData));
            }
            
            allUsers.push(userData);
          }
        } catch (e) {
          // Skip invalid entries
          console.error('Error parsing user data:', e);
        }
      }
    }
    
    return allUsers;
  } catch (error) {
    console.error('Error getting all users:', error);
    return [];
  }
};

/**
 * Get top users sorted by score, win rate, etc.
 * @param {Object} options - Filter options
 * @param {string} options.countryFilter - Filter by country
 * @param {number} options.limit - Maximum number of users to return
 * @returns {Array} Sorted array of users
 */
export const getTopUsers = (options = {}) => {
  try {
    const { countryFilter, limit = 10 } = options;
    let allUsers = getAllUsers();
    
    // Add current user if not in list yet
    const currentUser = getCurrentUser();
    if (currentUser && !allUsers.some(u => u.twitterUsername === currentUser.twitterUsername)) {
      allUsers.push(currentUser);
    }
    
    // Apply country filter if specified
    if (countryFilter) {
      allUsers = allUsers.filter(user => user.country === countryFilter);
    }
    
    // Sort by score, win rate, and games won
    const sortedUsers = allUsers.sort((a, b) => {
      // First sort by score
      if (a.score !== b.score) {
        return b.score - a.score; // Higher score first
      }
      
      // Then by win rate
      const aWinRate = a.gamesPlayed > 0 ? (a.gamesWon / a.gamesPlayed) : 0;
      const bWinRate = b.gamesPlayed > 0 ? (b.gamesWon / b.gamesPlayed) : 0;
      
      if (aWinRate !== bWinRate) {
        return bWinRate - aWinRate; // Higher win rate second
      }
      
      // Then by games won
      if (a.gamesWon !== b.gamesWon) {
        return b.gamesWon - a.gamesWon; // More wins third
      }
      
      // Finally by fewer games played
      return a.gamesPlayed - b.gamesPlayed; // Fewer games played fourth
    });
    
    // Apply limit if specified
    return limit ? sortedUsers.slice(0, limit) : sortedUsers;
  } catch (error) {
    console.error('Error getting top users:', error);
    return [];
  }
};

/**
 * Get user rank (global or by country)
 * @param {Object} options - Rank options
 * @param {string} options.username - Username to find rank for
 * @param {string} options.countryFilter - Filter by country
 * @returns {number|null} The user's rank (1-based) or null if not found
 */
export const getUserRank = (options = {}) => {
  try {
    const { username, countryFilter } = options;
    
    if (!username) {
      return null;
    }
    
    const allUsers = getTopUsers({ 
      countryFilter,
      limit: 0 // No limit, get all users
    });
    
    const rank = allUsers.findIndex(user => 
      user.twitterUsername === username) + 1;
      
    return rank > 0 ? rank : null;
  } catch (error) {
    console.error('Error getting user rank:', error);
    return null;
  }
};

/**
 * Get all countries from users
 * @returns {Array} Array of country names
 */
export const getAllCountries = () => {
  try {
    const allUsers = getAllUsers();
    const countries = new Set();
    
    allUsers.forEach(user => {
      if (user.country) {
        countries.add(user.country);
      }
    });
    
    return Array.from(countries).sort();
  } catch (error) {
    console.error('Error getting all countries:', error);
    return [];
  }
}; 